// The checkout's submit pipeline, shared by the drawer (CartPanel's
// CheckoutView) and the full-page stepped checkout: the booker's contact,
// consent, the ChiDesk per-treatment contacts, promo codes and gift cards,
// the totals, and submit() — the ChiDesk-only and mixed-cart paths, the
// code-covered net-zero redeem, the advance-deposit deferral, the payment
// intent and the Paystack hop, in today's commit order (answers → group
// answers → acceptances → intent, via ParticipantDetails' commit()). It
// exists ONCE: a second copy of a money path is how two surfaces start
// charging differently. Moved here verbatim from CheckoutView; the hold
// timer and keep-alive (HoldTimer, #1436) stay a component, reporting
// holdEnded back through setHoldEnded.
import type React from "react";
import { describeInitError } from "../../state/paystack-init-error";
import { useEffect, useId, useMemo, useRef, useState, useTransition } from "react";
import {
  GUEST_BOOKING_CONSENTS,
  LEGAL_DOCUMENT_VERSIONS,
  documentSlugForGuestConsent,
} from "@vertu/shared";
import type { WidgetTenant } from "../../types";
import { supabase } from "../../config";
import { lineGrossCents, type CartItem } from "../../state/cart";
import { useGuest } from "../../state/guest";
import { recordAttribution } from "../../state/attribution";
import { linkPromoFor, shouldApplyLinkPromo } from "../../state/link-promo";
import { consentWasInherited, setMarketingConsent, trackAdsConversion } from "../../state/tracking";
import { majorUnits, pushHostPurchase } from "../../state/host-datalayer";
import {
  type AppliedCode,
  type CodePreview,
  createBookingPaymentIntent,
  getBookingDepositState,
  redeemAndConfirmCart,
  getCartToken,
  initializePaystackCheckout,
  previewCode,
  setCartGuestContact,
  setCartGuestCountry,
} from "../../state/booking";
import {
  attachWidgetSpaPayload,
  bookChideskCart,
  createChideskDepositPaymentIntent,
  createMixedPaymentIntent,
  recheckChideskAvailability,
  previewChideskDeposit,
  savePendingChideskBooking,
  type ChideskBookItem,
} from "../../state/chidesk";
import type { ParticipantDetailsHandle } from "../ParticipantDetails";
import type { LoyaltySummary } from "../../state/loyalty";
import { normalizeOptionalPhone, normalizePhoneE164 } from "../../lib/phone";
import { computeCohostPairing } from "../../state/cohost";
import { LAPSE_MESSAGE } from "../../state/hold-timer-logic";
import {
  ALREADY_BOOKED_MESSAGE,
  ONE_MENU_PER_BOOKING_MESSAGE,
  minimumNotMetMessage,
} from "../../state/event-booking-rules";
import {
  type ChargeGuardNotice,
  chargeGuardNotice,
  checkEarlierCardPayments,
  explainLapse,
  offersBookAnother,
  payBlockedByGuard,
  refundedNotice,
  supersededNotice,
  watchEarlierPayment,
  watchesEarlierPayment,
} from "../../state/charge-guard";
import { keepPaystackHop } from "./useCheckoutResume";
import { COUNTRY_OPTIONS } from "./CheckoutParts";
import { formatDue, type CodeKind, type ReopenedFields, type ResumeGiven, type ResumePayment } from "../../state/checkout-steps";

export function isChideskItem(item: CartItem): boolean {
  return item.metadata?.source === "chidesk";
}

/**
 * True when a booking line's hold already charges the booking's whole price
 * (its own part, add-ons aside). Used only to NAME the checkout's rows.
 *
 * It is the cart line chip's test with the add-ons taken out first. The chip
 * compares amount_cents WITH add-ons, so a part-paid line plus add-ons that
 * reach the gross reads "Pay R X now" on the cart line while the checkout
 * (correctly) shows a Deposit row and a Full payment row for the add-ons.
 *
 * The hold RPCs send no "this is a deposit" signal, only amount_cents. For a
 * priced option the line's unitPrice IS the hold's own unit_price_cents
 * ((amount_cents − add-ons) ÷ guests, 20260915000050; buildUnitCartLine and
 * the cart's edit path both take it), so a full-price hold always reads
 * "Full payment" whatever price the page displayed. Only a hold that returns
 * no unit_price_cents (an unpriced option, whose amount IS a deposit; a
 * per-person total that does not divide by the party; an older RPC) is
 * measured against the displayed price, and there nothing tells a discounted
 * full price from a deposit, so it may read "Deposit". The rows still add up
 * to the same total.
 */
export function holdCoversWholePrice(item: CartItem, amountCents: number, addonCents: number): boolean {
  const gross = lineGrossCents(item);
  if (gross == null) return false;
  // lineGrossCents re-bases a room on amount_cents, add-ons included.
  const ownGross =
    item.serviceType === "accommodation" && gross === amountCents ? gross - addonCents : gross;
  return amountCents - addonCents >= ownGross - 1;
}

/**
 * What one Vertu cart line charges at checkout: its hold's amount (or, before
 * the hold answers, the displayed price), or nothing for a co-host sibling the
 * pairing makes bundled or free (charged 0 server-side). The checkout's total
 * is the sum of these, and the visit drawer asks the same question, so its
 * promo-code note never promises a code the checkout will not apply.
 */
export function checkoutLineCents(
  item: CartItem,
  siblingModes: ReadonlyMap<string, string | null | undefined>,
): number {
  const mode = siblingModes.get(item.id);
  if (mode === "bundled" || mode === "free_reserve") return 0;
  return (
    (item.metadata.amount_cents as number | null | undefined) ??
    Math.round((item.unitPrice ?? 0) * item.quantity * 100)
  );
}

// Per-item override details for ChiDesk items booked for family/friends.
// `forSomeoneElse=false` means "bill to the primary booker"; the server
// ignores the name/email fields in that case.
export interface PerItemContact {
  forSomeoneElse: boolean;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  /** Set on blur so the guest is told at the field, not at submit. */
  phoneError?: string | null;
}

/** What a confirmed checkout hands back (CartPanel's ConfirmedView snapshot). */
export type CheckoutConfirmPayload = {
  customerFirstName?: string;
  customerEmail?: string;
  customerPhone?: string;
  bookingReference?: string;
  amountCents?: number;
  /**
   * Does the server report this booking to GA4 and Meta itself (#1119: every
   * native booking going pending → confirmed, every paid one)? False for a
   * spa booking confirmed without a deposit: its board row is inserted
   * confirmed, so neither server rail fires. Absent: true.
   */
  serverReports?: boolean;
  deferredDeposit?: boolean;
  depositDueAt?: string;
  depositCents?: number;
  depositCurrency?: string;
};

/**
 * The browser is about to leave for Paystack with this payment, and what the
 * surface keeps with it (yes/no facts only, never an answer or a code):
 * which kinds of code were applied, the marketing tick, whether Special
 * requests or a Country other than the default were given, and whether the
 * page is an earlier page of this checkout the second-charge guard handed
 * back (resume) rather than a new one.
 */
export type PaystackHop = {
  reference: string;
  codesApplied: boolean;
  codeKinds: CodeKind[];
  /** The one code applied is the booking link's own (state/checkout-steps.ts CheckoutResume.linkCodeOnly). */
  linkCodeOnly: boolean;
  marketing: boolean;
  given: ResumeGiven;
  handedBack: boolean;
};

/** The trip a surface keeps for this hop (state/checkout-steps.ts: ResumePayment). */
export function hopPayment(hop: PaystackHop, now = Date.now()): ResumePayment {
  return { startedAt: now, reference: hop.reference, returned: false, ...(hop.handedBack ? { handedBack: true as const } : {}) };
}

/** A Country the checkout offers (COUNTRY_OPTIONS), else null. */
function offeredCountry(code: string | null | undefined): string | null {
  return code && COUNTRY_OPTIONS.some(([c]) => c === code) ? code : null;
}

export function useCheckoutSubmit({
  tenant,
  items,
  currency,
  onConfirm,
  onPaystackHop,
  reopened = null,
}: {
  tenant: WidgetTenant;
  items: CartItem[];
  currency: string;
  onConfirm: (payload?: CheckoutConfirmPayload) => void;
  /**
   * A checkout reopened after its trip to Paystack: Special requests and a
   * stay's Country as the unpaid payment holds them, the marketing tick as
   * the guest left it, and whether the next Pay asks for a new Paystack
   * page (freshPage). Read once, as the checkout opens.
   */
  reopened?: ReopenedFields | null;
  /**
   * Called synchronously just before the browser leaves for Paystack (the
   * payment exists and Paystack gave its page), never earlier: the surface
   * keeps its checkout with the reference (the full page all along, the
   * drawer from this moment), so the return asks about THIS payment and,
   * when Paystack says it was not paid, reopens Review & pay. The payment's
   * reference and the cart token are kept here for every surface
   * (leaveForPaystack), whatever the surface keeps.
   */
  onPaystackHop?: (hop: PaystackHop) => void;
}) {
  // Prefilled from the guest context: the identity sheet, a previous
  // checkout, or an OTP sign-in — a returning guest types nothing here.
  const { profile: guestProfile, saveProfile: saveGuestProfile } = useGuest();
  const [firstName, setFirstName] = useState(guestProfile.firstName);
  const [lastName, setLastName] = useState(guestProfile.lastName);
  const [email, setEmail] = useState(guestProfile.email);
  const [phone, setPhone] = useState(guestProfile.phone);
  // Inline validation for the mobile field — confirmations go out via
  // SMS/WhatsApp, which silently fail on non-international numbers.
  const [phoneError, setPhoneError] = useState<string | null>(null);
  // A reopened checkout starts from what the guest gave before: the same
  // Special requests make the next payment's guest details match the
  // earlier one's (the second-charge guard compares them), and a stay's
  // Country is not written back as South Africa.
  const [notes, setNotes] = useState(() => reopened?.notes ?? "");
  const [country, setCountry] = useState(() => offeredCountry(reopened?.country) ?? "ZA");
  // Country is only relevant to accommodation (drives the Cloudbeds
  // reservation) — don't ask spa/dining/event guests for it.
  const hasAccommodation = items.some((i) => i.serviceType === "accommodation");
  const participantSignature = items
    .filter((item) => !isChideskItem(item) && item.metadata?.booking_id)
    .map((item) => `${item.metadata.booking_id}:${item.metadata.party_size}`)
    .sort().join("|");
  const [participantsValid, setParticipantsValid] = useState(false);
  // Booking questions + checkout confirmations (ParticipantDetails): Proceed
  // writes the unsaved answers and the ticked statements before any money moves.
  const participantHandle = useRef<ParticipantDetailsHandle | null>(null);
  async function commitParticipantDetails(): Promise<boolean> {
    if (!participantSignature) return true;
    const problem = await participantHandle.current?.commit();
    if (problem) {
      setError(problem);
      return false;
    }
    return true;
  }
  // A payment refused for a missing answer or tick: say so in the forms' noun
  // and read them again, so what the server holds is what the guest sees.
  function confirmRefusal(
    reason: string,
    serviceName?: string | null,
    numbers?: RefusalNumbers,
  ): string {
    if (reason === "participant_details_required" || reason === "checkout_confirmations_required")
      participantHandle.current?.reload();
    if (reason === "hold_expired" || reason === "no_pending_bookings") setPaymentLapsed(true);
    return describeConfirmReason(reason, serviceName, participantHandle.current?.noun, numbers);
  }
  const [error, setError] = useState<string | null>(null);
  const [pending, startTransition] = useTransition();
  // The second-charge guard (20260928090040): an earlier payment for these
  // bookings went through, is still going, or cannot be checked. Said as a
  // notice, not an error (GuardNotice). "Went through" takes Pay away and
  // watches that payment until its confirmation is on file, then shows it;
  // if that payment is being refunded instead (paid after the hold ended),
  // the notice says so and Pay stays away (the guest chooses another time).
  const [guard, setGuard] = useState<ChargeGuardNotice | null>(null);
  const watchedReference = watchesEarlierPayment(guard) ? guard?.earlierReference ?? null : null;
  useEffect(() => {
    if (!watchedReference) return;
    return watchEarlierPayment(
      watchedReference,
      (result) => {
        // Paid on an earlier page: the venue's own tags (an embed on its
        // site) hear of the booking with what that payment took
        // (state/host-datalayer.ts).
        pushHostPurchase({
          tenant: tenant.slug,
          reference: result.primary_booking_reference ?? null,
          value: majorUnits(result.amount_cents),
          currency: result.currency ?? currency,
        });
        // Its Google Ads conversion, at what that payment took (the claim is
        // the return's own: a booking seen here and on the return address
        // converts once).
        trackAdsConversion({
          reference: result.primary_booking_reference ?? null,
          value: majorUnits(result.amount_cents) ?? 0,
          currency: result.currency ?? currency,
          email: result.customer.email ?? null,
        });
        onConfirm({
          customerFirstName: result.customer.first_name ?? undefined,
          customerEmail: result.customer.email ?? undefined,
          customerPhone: result.customer.phone ?? undefined,
          bookingReference: result.primary_booking_reference ?? undefined,
        });
      },
      {
        onRefunded: (bookingConfirmed, inFull) =>
          setGuard(refundedNotice(bookingConfirmed, watchedReference, inFull)),
        // Paid on an out-of-date page (20261003200010): refunded in full,
        // and Pay comes back for the updated total.
        onSuperseded: (answer) => setGuard(supersededNotice(answer, watchedReference)),
      },
    );
    // onConfirm is a fresh closure each render; the watch is per reference.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watchedReference]);
  // A refused intent. "Your hold ended" is only said once the page this tab
  // last sent the guest to is known not to have been paid in that window.
  async function refuseWith(
    reason: string,
    serviceName?: string | null,
    numbers?: RefusalNumbers,
  ): Promise<void> {
    if (reason === "hold_expired" || reason === "no_pending_bookings") {
      const lapse = await explainLapse(getCartToken());
      if (lapse) {
        setGuard(lapse);
        return;
      }
    }
    setError(confirmRefusal(reason, serviceName, numbers));
  }
  // Leaving for Paystack with this payment. This tab keeps its reference
  // and the cart token that proves the tab owns it (never an answer, the
  // booker's contact or a code), so the return asks Paystack at once
  // (paystack-payment-status) instead of waiting for the webhook; then the
  // surface keeps what it keeps (onPaystackHop), and the browser leaves.
  // `requested`: the reference Pay asked for a page for; the guard may hand
  // back an earlier page of this checkout instead (handedBack).
  function leaveForPaystack(init: { reference: string; authorization_url: string }, requested: string): void {
    keepPaystackHop(tenant.slug, init.reference, getCartToken());
    const kinds = new Set(appliedCodes.map((c): CodeKind => (c.kind === "gift_card" ? "gift_card" : "promo")));
    onPaystackHop?.({
      reference: init.reference,
      codesApplied: appliedCodes.length > 0,
      codeKinds: (["promo", "gift_card"] as const).filter((k) => kinds.has(k)),
      linkCodeOnly: onlyLinkCode,
      marketing: consentMarketing,
      given: { notes: notes.trim() !== "", country: hasAccommodation && country !== "ZA" },
      handedBack: init.reference !== requested,
    });
    window.location.href = init.authorization_url;
  }
  function refuseInit(init: { error: string; message?: string; earlier_reference?: string }): void {
    const notice = chargeGuardNotice(init.error, init.message, init.earlier_reference);
    if (notice) setGuard(notice);
    else setError(describeInitError(init.error, init.message));
  }
  // The checkout form: HoldTimer counts a key press, tap or edit in it as the
  // guest still being here.
  const checkoutFormRef = useRef<HTMLFormElement | null>(null);
  // "Book another" on the same-slot notice (same_slot_already_paid): this
  // guest already paid for this slot on another cart, and this ONE press
  // makes a second booking on purpose. submit() reads it synchronously, so
  // it never outlives the press (nor a press the form's validation stops).
  const allowSameSlotNext = useRef(false);
  function bookAnother(): void {
    if (!offersBookAnother(guard)) return;
    allowSameSlotNext.current = true;
    try {
      checkoutFormRef.current?.requestSubmit();
    } finally {
      allowSameSlotNext.current = false;
    }
  }
  // The server confirmed the hold ended (lapsed, or every row gone): Pay is
  // off, and the banner offers "Back to your visit". The server refuses the
  // payment too (hold_expired, 20260927156010), so this only says it sooner.
  const [holdEnded, setHoldEnded] = useState(false);
  // The server refused the payment because a hold on this cart ended
  // (hold_expired / no_pending_bookings, 20260927156510) before the hold
  // timer said so: Pay stays off here too. The "Back to your visit" link
  // stays, since removing or re-adding the line there is the way on (going
  // back unmounts this view, so a fresh checkout starts enabled).
  const [paymentLapsed, setPaymentLapsed] = useState(false);

  // Promo-code / gift-card state. Codes validate against the first
  // booking in the cart (widget rule for MVP — admin can issue
  // compensating adjustments if a guest expected it on a different item).
  const [appliedCodes, setAppliedCodes] = useState<AppliedCode[]>([]);
  const [codeInput, setCodeInput] = useState("");
  const [codeError, setCodeError] = useState<string | null>(null);
  const [codePending, setCodePending] = useState(false);
  // Loyalty redemption — single-shot for MVP. State lifted from the
  // banner so the redemption control + total deduction share one
  // source of truth.
  const [loyaltySummary, setLoyaltySummary] = useState<LoyaltySummary | null>(
    null,
  );
  const [appliedRedemption, setAppliedRedemption] = useState<
    { points: number; centsOff: number; newBalance: number } | null
  >(null);
  // "Your details": each label names its own box (label for / id).
  const contactId = useId();
  const firstBookingId = useMemo(() => {
    for (const it of items) {
      if (it.metadata?.source !== "chidesk" && typeof it.metadata?.booking_id === "string") {
        return it.metadata.booking_id as string;
      }
    }
    return null;
  }, [items]);
  /**
   * Human-friendly reference of the first non-ChiDesk booking in the
   * cart. Surfaced by create_booking_hold from
   * 20260516000003_booking_reference.sql. Threaded through onConfirm so
   * ConfirmedView can render it ("Booking reference: LAB-260516-K3PTRN").
   * Null when the cart only has ChiDesk items (their reference comes
   * back later via the bookChideskCart flow and isn't surfaced yet).
   */
  const firstBookingReference = useMemo(() => {
    for (const it of items) {
      if (it.metadata?.source !== "chidesk" && typeof it.metadata?.booking_reference === "string") {
        return it.metadata.booking_reference as string;
      }
    }
    return null;
  }, [items]);

  const chideskItems = useMemo(() => items.filter(isChideskItem), [items]);
  const vertuItems = useMemo(() => items.filter((i) => !isChideskItem(i)), [items]);
  const isChideskOnly = chideskItems.length > 0 && vertuItems.length === 0;
  const isMixedCart = chideskItems.length > 0 && vertuItems.length > 0;
  // Whether this checkout takes a promo code or gift card: the code box shows,
  // and a code from the booking link is applied, on this one rule. A spa-only
  // cart pays at the venue; a mixed cart's intent (create_mixed_payment_intent)
  // ignores codes, so offering one there would show a total it never charges.
  const codesAccepted = !isChideskOnly && !isMixedCart && firstBookingId !== null;

  // Co-hosting (Phase B) — same pairing the cart view ran: zeroed
  // bundled/free_reserve siblings drop out of the due-now sum (mirroring
  // the server's zeroing) and an unpaired REQUIRED ticket blocks submit.
  // Soft mirror only: the server re-runs this gate inside the money RPCs.
  const cohostPairing = useMemo(() => computeCohostPairing(items), [items]);
  const cohostUnmet = cohostPairing.unmet[0] ?? null;

  // Guest consent capture. Mandatory privacy/terms tick is required to
  // submit; marketing opt-in is genuinely optional and defaults off
  // (POPIA + GDPR — never pre-tick optional consents). Both rows are
  // logged via log_consent_anon at submit time so we have a record of
  // the negative answer too (false), not just the positive ones.
  //
  // The ONE exception: the guest arrived from the tenant's own site having
  // already granted marketing consent to that same data controller, and the
  // site tag said so (?vt_consent=1 → consentWasInherited). Starting unticked
  // there does not protect anyone — it silently converts an existing grant
  // into a logged refusal. Reflect what they already said and let them change
  // it; a fresh visit with no inherited grant still starts unticked.
  const [consentTerms, setConsentTerms] = useState(false);
  // A checkout reopened after its trip to Paystack shows the tick as the
  // guest left it in this very checkout (never a choice from another visit),
  // so a second Pay never records a "no" they did not give.
  const [consentMarketing, setConsentMarketing] = useState(() =>
    typeof reopened?.marketing === "boolean" ? reopened.marketing : consentWasInherited(),
  );

  // Keep a per-item contact entry for every ChiDesk item — keyed on cart
  // item id so adding / removing items cleans up automatically.
  const [perItem, setPerItem] = useState<Record<string, PerItemContact>>({});
  const ensureContact = (id: string): PerItemContact =>
    perItem[id] ?? {
      forSomeoneElse: false,
      firstName: "",
      lastName: "",
      email: "",
      phone: "",
      phoneError: null,
    };
  const patchContact = (id: string, patch: Partial<PerItemContact>) => {
    setPerItem((prev) => ({
      ...prev,
      [id]: { ...ensureContact(id), ...patch },
    }));
  };

  // Three buckets so the breakdown names what is actually charged (one
  // "Deposits" row made no sense for tickets or anything paid in full):
  //   ticketCents   — event-ticket items (metadata.source = 'event'),
  //                   always pay-in-full, labelled "Tickets"
  //   requiredCents — everything else charged IN FULL now: fixed-menu /
  //                   special-date bookings, add-ons, and any booking whose
  //                   hold already costs its whole price, labelled "Full
  //                   payment". The flag alone is not enough: the server
  //                   sets requires_full_payment only for special dates, so
  //                   an option unit priced in full (a Fijnland ride) holds
  //                   its whole price with the flag false.
  //   depositCents  — the part-payment of a booking whose balance is due at
  //                   the venue, labelled "Deposit" (one) / "Deposits".
  // Only the labels depend on this split; the rows always add up to the
  // same total.
  const { ticketCents, requiredCents, depositCents, depositLines } = useMemo(() => {
    let tix = 0;
    let req = 0;
    let dep = 0;
    let depLines = 0;
    for (const it of vertuItems) {
      // Cohost-paired bundled/free_reserve sibling: charged 0 server-side —
      // keep it out of the buckets so "Due now" matches the real intent.
      const mode = cohostPairing.siblingModes.get(it.id);
      if (mode === "bundled" || mode === "free_reserve") continue;
      const amt = checkoutLineCents(it, cohostPairing.siblingModes);
      // Checkout add-ons inside amount_cents are FULLY paid now — split them
      // out of the deposit bucket so "Deposits · balance at the venue" only
      // covers the service's own partial payment.
      const addonCents = (it.metadata.addons_cents as number | undefined) ?? 0;
      if (it.metadata.source === "event") {
        tix += amt ?? 0;
      } else if (it.metadata.requires_full_payment || (amt && holdCoversWholePrice(it, amt, addonCents))) {
        req += amt ?? 0;
      } else if (amt) {
        const own = Math.max(0, amt - addonCents);
        dep += own;
        if (own > 0) depLines += 1;
        req += Math.min(amt, addonCents);
      }
    }
    return { ticketCents: tix, requiredCents: req, depositCents: dep, depositLines: depLines };
  }, [vertuItems, cohostPairing]);

  const totalCents = ticketCents + requiredCents + depositCents;

  // Discount preview from applied codes + loyalty points. Clamped so the
  // running total never goes below zero. Order: loyalty redemption first
  // (locked in server-side via the redeem RPC), then voucher/gift-card
  // codes stack on top.
  const discountCents = useMemo(() => {
    let remaining = totalCents;
    let total = 0;
    if (appliedRedemption) {
      const applied = Math.min(appliedRedemption.centsOff, remaining);
      total += applied;
      remaining -= applied;
    }
    for (const c of appliedCodes) {
      if (remaining <= 0) break;
      const applied = Math.min(c.discount_cents, remaining);
      total += applied;
      remaining -= applied;
    }
    return total;
  }, [appliedCodes, appliedRedemption, totalCents]);

  // Gross treatment value in the cart. Surfaced alongside the deposit so
  // the customer sees both the "due now" deposit and the "balance at the
  // venue" amount.
  const chideskTotalCents = useMemo(
    () =>
      chideskItems.reduce(
        // amount_cents is the per-person treatment price; quantity is the party
        // size — multiply so the gross matches the line total, the (qty-aware)
        // deposit, and the client-side purchase conversion value.
        (n, it) =>
          n + ((it.metadata.amount_cents as number | undefined) ?? 0) * (it.quantity || 1),
        0,
      ),
    [chideskItems],
  );

  // Deposit due at checkout. Computed server-side from the per-treatment
  // deposit rules — we re-fetch on every chidesk cart change. `null` means
  // "still loading"; `0` means "no deposit configured / required".
  // True when the spa deposit preview failed — mixed carts must not price
  // themselves without it (see the fail-closed comment below).
  const [depositPreviewFailed, setDepositPreviewFailed] = useState(false);
  const [chideskDepositCents, setChideskDepositCents] = useState<number | null>(
    chideskItems.length > 0 ? null : 0,
  );
  useEffect(() => {
    // Fetch the deposit for ANY cart with spa items — spa-only AND mixed. For
    // mixed carts create_mixed_payment_intent charges native + this deposit, so
    // the checkout total MUST include it or the customer is charged more than
    // the button shows.
    if (chideskItems.length === 0) {
      setChideskDepositCents(0);
      return;
    }
    let cancelled = false;
    const items = chideskItems.map((it) => ({
      treatment_row_id: String(
        (it.metadata as Record<string, unknown>).chidesk_treatment_id ?? "",
      ),
      qty: it.quantity || 1,
    }));
    setChideskDepositCents(null);
    setDepositPreviewFailed(false);
    previewChideskDeposit(items).then((res) => {
      if (cancelled) return;
      // On failure keep the deposit UNKNOWN (null), never 0. The submit button
      // is already gated on `isMixedCart && chideskDepositCents === null`, but
      // falling back to 0 defeated that guard AND understated the total: a
      // mixed cart would show "Pay R<native only>" while
      // create_mixed_payment_intent computes its own spa deposit server-side
      // (v_amount := v_native_cents + v_spa_deposit) and charges MORE than the
      // button said. Fail closed and ask the guest to retry instead.
      setChideskDepositCents(res.ok ? res.total_cents : null);
      setDepositPreviewFailed(!res.ok);
    });
    return () => {
      cancelled = true;
    };
  }, [chideskItems]);

  async function recordGuestConsent(emailValue: string) {
    // Persist the marketing-consent choice so the client-side ad tags honour
    // it (the consent gate reads this from localStorage).
    setMarketingConsent(consentMarketing);
    // Best-effort. We don't block the booking on a consent log write
    // failing — the audit trail elsewhere (booking row, payment ref)
    // already pins the moment. But under normal conditions both rows
    // land before the booking RPC is called.
    const trimmedEmail = emailValue.trim();
    if (!trimmedEmail) return;
    const rows = [
      {
        key: "privacy_and_terms" as const,
        def: GUEST_BOOKING_CONSENTS.privacy_and_terms,
        granted: consentTerms,
      },
      {
        key: "marketing_optin" as const,
        def: GUEST_BOOKING_CONSENTS.marketing_optin,
        granted: consentMarketing,
      },
    ];
    // A consent write that fails must not pass silently: supabase.rpc resolves
    // with { error } rather than throwing, so Promise.all swallowed a failed
    // POPIA record entirely (including mandatory terms). Surfacing it in the
    // console is the minimum — a deploy-order mismatch, a renamed signature or
    // an RLS change would otherwise look exactly like success.
    const consentResults = await Promise.all(
      rows.map((r) => {
        // Single source of truth for the checkbox→document mapping (shared
        // with the consent-log audit). Marketing opt-in stamps the guest's own
        // `guest_marketing` slug and version, not `privacy_policy`.
        const slug = documentSlugForGuestConsent(r.key);
        return supabase.rpc("log_consent_anon", {
          p_email: trimmedEmail,
          p_tenant_id: tenant.id,
          p_document_slug: slug,
          p_document_version: LEGAL_DOCUMENT_VERSIONS[slug],
          p_checkbox_text: r.def.text,
          p_consent_kind: r.def.kind,
          p_granted: r.granted,
          p_metadata: { source: "widget_checkout" },
          // Possession proof: the cart carrying this guest's email. Consent
          // logged without it records as UNVERIFIED and cannot authorise
          // sending the guest's PII to ad platforms.
          p_cart_token: getCartToken(),
        });
      }),
    );
    for (const res of consentResults) {
      if (res?.error) {
        console.error("[vertu] consent write failed", res.error);
      }
    }
  }

  function submit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const allowSameSlot = allowSameSlotNext.current;
    allowSameSlotNext.current = false;
    if (payBlockedByGuard(guard)) return;
    setError(null);
    setGuard(null);
    if (holdEnded) return;

    if (participantSignature && !participantsValid) {
      setError(
        participantHandle.current?.issue() ??
          "Please complete and save each guest's details below before continuing.",
      );
      return;
    }

    if (!consentTerms) {
      setError(
        "Please confirm that you accept the venue's privacy notice and the Vertu Guest Terms of Use to continue.",
      );
      return;
    }

    // Mobile numbers must normalise to international (E.164) format before
    // anything is charged or booked — SMS / WhatsApp confirmations silently
    // fail on local-format numbers.
    const phoneCheck = normalizePhoneE164(phone);
    if (!phoneCheck.ok) {
      setPhoneError(phoneCheck.error ?? null);
      setError(phoneCheck.error ?? "Please check your mobile number.");
      return;
    }
    const phoneE164 = phoneCheck.e164!;
    setPhoneError(null);
    if (phone !== phoneE164) setPhone(phoneE164);
    // Round-trip the typed details into the guest context: they persist for
    // the next visit, and the holds in this cart get stamped so an abandoned
    // payment still names its owner.
    saveGuestProfile({
      firstName: firstName.trim(),
      lastName: lastName.trim(),
      email: email.trim(),
      phone: phoneE164,
    });
    // Per-treatment guest contacts: the number is optional, but a filled-in
    // one gets its own confirmation, so it must be valid too.
    for (const contact of Object.values(perItem)) {
      if (!contact?.forSomeoneElse || !contact.phone.trim()) continue;
      const check = normalizePhoneE164(contact.phone);
      if (!check.ok) {
        // Use the normaliser's own message. The generic "needs international
        // format" line reads as nonsense to someone who typed "+825727456" —
        // they DID use a plus. Only the specific message explains it.
        setError(
          `Mobile number for ${contact.firstName.trim() || "your guest"}: ${
            check.error ??
            "needs international format, e.g. +27 82 123 4567."
          }`,
        );
        return;
      }
    }

    // Co-hosting: no client-side block. The tenant's unavailable_fallback
    // isn't exposed to the widget, and sell_notify (the default) means the
    // sale must proceed even without the sibling — the SERVER blocks only
    // for fallback='block' tenants ('cohost_required', surfaced below).

    // ── Mixed cart: native items + ChiDesk spa in ONE payment ──
    // One intent covers the native cart total + the ChiDesk deposit. The spa
    // booking payload is persisted server-side; the webhook books it after
    // payment (create_mixed_payment_intent + paystack-webhook), so it survives
    // a closed tab. The native return leg renders the confirmation; the spa
    // sends its own booking confirmation from the proxy.
    if (isMixedCart) {
      startTransition(async () => {
        if (!(await commitParticipantDetails())) return;
        await recordGuestConsent(email);
        const trimmedEmail = email.trim();
        const integrationId = chideskItems[0]?.metadata?.chidesk_integration_id as
          | string
          | undefined;
        if (!integrationId) {
          setError("We couldn't identify the spa integration for this booking.");
          return;
        }

        const bookItems: ChideskBookItem[] = chideskItems.map((it) => {
          const override = perItem[it.id];
          const m = it.metadata as Record<string, unknown>;
          return {
            treatment_row_id: String(m.chidesk_treatment_id ?? ""),
            chidesk_treatment_guid: String(m.chidesk_treatment_guid ?? ""),
            chidesk_staff_guid: (m.chidesk_staff_guid as string | null) ?? null,
            chidesk_staff_row_id: (m.chidesk_staff_id as string | null) ?? null,
            chidesk_staff_candidates:
              (m.chidesk_staff_candidates as ChideskBookItem["chidesk_staff_candidates"]) ??
              undefined,
            starts_at: String(m.starts_at ?? ""),
            ends_at: String(m.ends_at ?? ""),
            duration_minutes: (m.duration_minutes as number | undefined) ?? 60,
            amount_cents: (m.amount_cents as number | undefined) ?? 0,
            deposit_cents: (m.deposit_cents as number | undefined) ?? 0,
            currency: (m.currency as string | undefined) ?? it.currency ?? currency,
            treatment_name: (m.treatment_name as string | undefined) ?? it.description,
            for:
              override?.forSomeoneElse && override.email.trim()
                ? {
                    first_name: override.firstName.trim(),
                    last_name: override.lastName.trim(),
                    email: override.email.trim(),
                    phone: normalizeOptionalPhone(override.phone).e164,
                  }
                : null,
          };
        });
        const depositItems = chideskItems.map((it) => ({
          treatment_row_id: String(
            (it.metadata as Record<string, unknown>).chidesk_treatment_id ?? "",
          ),
          qty: it.quantity || 1,
        }));

        const cartToken = getCartToken();
        await recordAttribution({ cartToken });
        if (hasAccommodation && country) {
          await setCartGuestCountry({ cartToken, country });
        }

        // Pre-payment re-check: hand a just-taken slot back to the guest BEFORE
        // charging the deposit (change/remove in the Spa tab) — no refund churn.
        {
          const recheck = await recheckChideskAvailability({
            tenantIntegrationId: integrationId,
            items: bookItems,
          });
          const gone = recheck.results.filter((r) => !r.available);
          if (gone.length > 0) {
            const names = gone.map((g) => g.treatment_name).join(", ");
            setError(
              `${names} ${gone.length === 1 ? "is" : "are"} no longer available at that time — someone just booked it. Pick another time or therapist in the Spa tab, or remove ${gone.length === 1 ? "it" : "them"} to continue.`,
            );
            return;
          }
        }

        const intent = await createMixedPaymentIntent({
          cartToken,
          guest: {
            name: `${firstName} ${lastName}`.trim(),
            email: trimmedEmail,
            phone: phoneE164,
            notes: notes.trim() || undefined,
          },
          chideskIntegrationId: integrationId,
          chideskItems: bookItems,
          chideskDepositItems: depositItems,
          chideskPrimary: {
            first_name: firstName.trim(),
            last_name: lastName.trim(),
            email: trimmedEmail,
            phone: phoneE164,
          },
        });
        if (!intent.ok) {
          await refuseWith(intent.reason, intent.service_name, refusalNumbers(intent));
          return;
        }
        const init = await initializePaystackCheckout({
          reference: intent.reference,
          customerEmail: trimmedEmail,
          callbackUrl: window.location.href,
          allowSameSlot,
        });
        if (!init.ok) {
          refuseInit(init);
          return;
        }
        // The page's reference, which can be an earlier page of this very
        // checkout the server handed back (20260928090040).
        leaveForPaystack(init, intent.reference);
      });
      return;
    }

    // ── ChiDesk-only path ──
    if (isChideskOnly) {
      startTransition(async () => {
        await recordGuestConsent(email);
        const trimmedEmail = email.trim();
        const integrationId = chideskItems[0]?.metadata?.chidesk_integration_id as
          | string
          | undefined;
        if (!integrationId) {
          setError("We couldn't identify the spa integration for this booking.");
          return;
        }

        const bookItems: ChideskBookItem[] = chideskItems.map((it) => {
          const override = perItem[it.id];
          const m = it.metadata as Record<string, unknown>;
          return {
            treatment_row_id: String(m.chidesk_treatment_id ?? ""),
            chidesk_treatment_guid: String(m.chidesk_treatment_guid ?? ""),
            chidesk_staff_guid: (m.chidesk_staff_guid as string | null) ?? null,
            chidesk_staff_row_id: (m.chidesk_staff_id as string | null) ?? null,
            chidesk_staff_candidates:
              (m.chidesk_staff_candidates as ChideskBookItem["chidesk_staff_candidates"]) ??
              undefined,
            starts_at: String(m.starts_at ?? ""),
            ends_at: String(m.ends_at ?? ""),
            duration_minutes:
              (m.duration_minutes as number | undefined) ?? 60,
            amount_cents: (m.amount_cents as number | undefined) ?? 0,
            deposit_cents: (m.deposit_cents as number | undefined) ?? 0,
            currency:
              (m.currency as string | undefined) ?? it.currency ?? currency,
            treatment_name:
              (m.treatment_name as string | undefined) ?? it.description,
            for:
              override?.forSomeoneElse && override.email.trim()
                ? {
                    first_name: override.firstName.trim(),
                    last_name: override.lastName.trim(),
                    email: override.email.trim(),
                    phone: normalizeOptionalPhone(override.phone).e164,
                  }
                : null,
          };
        });

        const cartToken = getCartToken();
        const primary = {
          first_name: firstName.trim(),
          last_name: lastName.trim(),
          email: trimmedEmail,
          phone: phoneE164,
        };

        // Pre-payment re-check: hand a just-taken slot back to the guest BEFORE
        // charging the deposit (change/remove in the Spa tab) — no refund churn.
        {
          const recheck = await recheckChideskAvailability({
            tenantIntegrationId: integrationId,
            items: bookItems,
          });
          const gone = recheck.results.filter((r) => !r.available);
          if (gone.length > 0) {
            const names = gone.map((g) => g.treatment_name).join(", ");
            setError(
              `${names} ${gone.length === 1 ? "is" : "are"} no longer available at that time — someone just booked it. Pick another time or therapist in the Spa tab, or remove ${gone.length === 1 ? "it" : "them"} to continue.`,
            );
            return;
          }
        }

        // Persist the cart's campaign data before an intent/payment exists.
        // This is awaited because the next successful step may navigate away.
        await recordAttribution({ cartToken });

        // Deposit gate. If the cart attracts a deposit we have to charge
        // it through Paystack first — book happens on the return leg
        // after the webhook flips the payments row to 'success'. With no
        // deposit (or `no_payment_required`) we book directly, same as
        // before.
        const intent = await createChideskDepositPaymentIntent({
          tenantIntegrationId: integrationId,
          items: chideskItems.map((it) => ({
            treatment_row_id: String(
              (it.metadata as Record<string, unknown>).chidesk_treatment_id ?? "",
            ),
            qty: it.quantity || 1,
          })),
          customerEmail: trimmedEmail,
          customerPhone: phoneE164 || undefined,
          cartToken,
        });
        if (!intent.ok) {
          setError(`Couldn't start checkout (${intent.reason}).`);
          return;
        }

        // A paid ChiDesk intent just created the legacy payments target. Stamp
        // it too for deploy-order compatibility; the recorder is write-once,
        // and the cart-level write above remains the durable primary capture.
        if (intent.reference) {
          await recordAttribution({ cartToken });
        }

        if (intent.no_payment_required || !intent.reference) {
          // No deposit — straight to bookChideskCart, no Paystack hop.
          const result = await bookChideskCart({
            tenantIntegrationId: integrationId,
            cartToken,
            primary,
            items: bookItems,
          });
          if ("results" in result) {
            if (result.ok) {
              // First successful row's reference (surfaced by the
              // chidesk-proxy after migration 20260516000006). Multi-
              // treatment carts get one reference per appointment; we
              // show the first in ConfirmedView's headline slot. Falls
              // back to undefined on pre-migration deployments — the
              // confirmation view just renders the tenant-name fallback
              // in that case.
              const firstReference =
                result.results.find((r) => r.ok && r.reference)?.reference ??
                undefined;
              onConfirm({
                customerFirstName: firstName.trim() || undefined,
                customerEmail: email.trim() || undefined,
                customerPhone: phoneE164 || undefined,
                bookingReference: firstReference ?? undefined,
                // Chidesk confirms in-widget (no Paystack redirect), so the
                // client-side purchase fires here with the gross treatment value.
                amountCents: chideskTotalCents,
                // No server twin: the spa board row is inserted confirmed.
                serverReports: false,
              });
              return;
            }
            const failed = result.results.find((r) => !r.ok);
            setError(
              failed
                ? `Couldn't book "${failed.treatment_name}": ${failed.error ?? "unknown error"}`
                : "Some treatments couldn't be booked.",
            );
            return;
          }
          setError(
            `Couldn't reach the spa booking system${result.error ? ` (${result.error})` : ""}.`,
          );
          return;
        }

        // Deposit path: stash everything we need to finalise on return,
        // then redirect to Paystack. callback_url carries the reference
        // so the resume handler can pick the cart back up.
        savePendingChideskBooking({
          tenantIntegrationId: integrationId,
          cartToken,
          primary,
          items: bookItems,
          quantities: chideskItems.map((it) => it.quantity || 1),
          reference: intent.reference,
          savedAt: Date.now(),
        });
        // The same payload, server-side, so a settled deposit books even if
        // this tab never comes back. Awaited (it is one fast RPC) because the
        // return leg reads finalize_status to decide who owns the booking —
        // but a failure only means the browser keeps ownership, as before, so
        // it never blocks the redirect.
        await attachWidgetSpaPayload({
          reference: intent.reference,
          cartToken,
          tenantIntegrationId: integrationId,
          items: bookItems,
          primary,
        });

        const callbackUrl = (() => {
          const url = new URL(window.location.href);
          url.searchParams.set("vt_chidesk_ref", intent.reference!);
          return url.toString();
        })();
        const init = await initializePaystackCheckout({
          reference: intent.reference,
          customerEmail: trimmedEmail,
          callbackUrl,
          allowSameSlot,
        });
        if (!init.ok) {
          refuseInit(init);
          return;
        }
        window.location.href = init.authorization_url;
      });
      return;
    }

    // ── Vertu-only (original) path ──
    startTransition(async () => {
      if (!(await commitParticipantDetails())) return;
      await recordGuestConsent(email);
      // Attach ad-click/campaign attribution to this cart's bookings for
      // conversion reporting (best-effort, before payment).
      await recordAttribution({ cartToken: getCartToken() });
      if (hasAccommodation && country) {
        await setCartGuestCountry({ cartToken: getCartToken(), country });
      }
      const guest = {
        name: `${firstName} ${lastName}`.trim(),
        email: email.trim(),
        phone: phoneE164,
        notes: notes.trim() || undefined,
      };
      // A promo code's one-use-per-guest limit counts for the guest the held
      // lines' contact matches when the hold has no guest yet (validate_voucher,
      // 20261014100010). saveGuestProfile stamps that contact in the background;
      // with a code applied, stamp it and wait before the code is checked again.
      if (appliedCodes.length > 0) {
        await setCartGuestContact({
          cartToken: getCartToken(),
          name: guest.name || null,
          email: guest.email || null,
          phone: guest.phone || null,
        });
      }

      // Code-covered net-zero: codes/gift-card drive the payable to R0. Paystack
      // can't charge R0, so redeem the codes ATOMICALLY and confirm in-widget
      // (redeem-first). Only when the preview net is 0 and codes are applied —
      // a raw-free cart (no codes) still uses the intent's v_all_free path. On a
      // rare race where the net turns out > 0, the server rolls the redemption
      // back and returns needs_payment; fall through to the normal Paystack hop.
      if (vertuNetCents === 0 && appliedCodes.length > 0) {
        const redeem = () =>
          redeemAndConfirmCart({
            cartToken: getCartToken(),
            guest,
            codes: appliedCodes.map((c) => c.code),
            allowSameSlot,
          });
        let rc = await redeem();
        // A card payment for this cart (or this guest's slot on a cart a
        // reload left behind) reached Paystack and Paystack has not said it
        // failed (20260928090040): ask about it first. That records
        // Paystack's answer, so when none was paid the confirm goes through.
        // A slot this guest already paid for on another cart is never "not
        // charged": that answer offers "Book another" instead.
        if (!rc.ok && rc.reason === "earlier_payment_unverified") {
          const notice = await checkEarlierCardPayments(
            getCartToken(),
            { email: guest.email, phone: guest.phone },
            { allowSameSlot },
          );
          if (notice) {
            setGuard(notice);
            return;
          }
          rc = await redeem();
        }
        if (!rc.ok && rc.reason === "earlier_payment_succeeded") {
          setGuard(chargeGuardNotice("earlier_payment_succeeded", null, rc.reference));
          return;
        }
        if (!rc.ok && rc.reason === "same_slot_already_paid") {
          setGuard(chargeGuardNotice("same_slot_already_paid"));
          return;
        }
        if (!rc.ok) {
          await refuseWith(rc.reason, rc.service_name, refusalNumbers(rc));
          return;
        }
        if (rc.no_payment_required) {
          onConfirm({
            customerFirstName: firstName.trim() || undefined,
            customerEmail: email.trim() || undefined,
            customerPhone: phoneE164 || undefined,
            bookingReference: firstBookingReference ?? undefined,
            amountCents: 0,
          });
          return;
        }
        // rc.needs_payment (race) — nothing consumed; continue to Paystack below.
      }

      // Advance-deposit deferral: if the server-side policy turned this booking
      // into a managed hold (a large group booked far ahead — reserve now, pay
      // the deposit later), DON'T charge at checkout. Show a "reserved — deposit
      // due" confirmation; the Phase-D cron sends the deposit pay-link at the due
      // date. Any read error falls through to normal checkout.
      if (firstBookingId) {
        const dep = await getBookingDepositState(firstBookingId, getCartToken());
        if (dep.ok && dep.deferred) {
          onConfirm({
            customerFirstName: firstName.trim() || undefined,
            customerEmail: email.trim() || undefined,
            customerPhone: phoneE164 || undefined,
            bookingReference: firstBookingReference ?? undefined,
            deferredDeposit: true,
            depositDueAt: dep.deposit_due_at ?? undefined,
            depositCents: dep.amount_cents,
            depositCurrency: dep.currency,
          });
          return;
        }
      }

      const intent = await createBookingPaymentIntent({
        cartToken: getCartToken(),
        guest,
        codes: appliedCodes.map((c) => c.code),
      });
      if (!intent.ok) {
        await refuseWith(intent.reason, intent.service_name, refusalNumbers(intent));
        return;
      }
      if (intent.no_payment_required) {
        // Free cart (zero-total bookings) — the server already confirmed the
        // bookings (Paystack can't charge R0). Go straight to confirmation, the
        // same way the ChiDesk no-deposit path books in-widget. Use the human
        // booking reference (falls back to the tenant name in ConfirmedView),
        // never the internal payment token.
        onConfirm({
          customerFirstName: firstName.trim() || undefined,
          customerEmail: email.trim() || undefined,
          customerPhone: phoneE164 || undefined,
          bookingReference: firstBookingReference ?? undefined,
          amountCents: 0,
        });
        return;
      }
      const init = await initializePaystackCheckout({
        reference: intent.reference,
        customerEmail: email.trim(),
        callbackUrl: window.location.href,
        allowSameSlot,
        // The trip's page was an earlier page handed back once already, and
        // Paystack said it was not paid: ask for a new page, never that one
        // again (a page the guest cancelled may not take a payment, and the
        // guest would go round in a loop). The guard still asks Paystack
        // about it first.
        allowResume: reopened?.freshPage !== true,
      });
      if (!init.ok) {
        refuseInit(init);
        return;
      }
      // The page's reference, which can be an earlier page of this very
      // checkout the server handed back (20260928090040).
      leaveForPaystack(init, intent.reference);
    });
  }

  const chideskHasDeposit =
    chideskDepositCents != null && chideskDepositCents > 0;
  // Net payable for the Vertu path after any promo/gift-card discount.
  const vertuNetCents = Math.max(0, totalCents - discountCents);
  // Mixed cart charges the native net + the ChiDesk deposit in one payment;
  // the spa treatment balance is settled at the venue.
  const mixedDueNowCents = vertuNetCents + (chideskDepositCents ?? 0);
  // A genuinely free cart is one whose RAW booking total is zero (a price-0 or
  // discount-option-zeroed event ticket) — it confirms in-widget with no
  // Paystack. Gate on the raw total, matching the server (which confirms only
  // when every booking row is 0), NOT on the discount-reduced net: a paying
  // cart merely discounted to zero by a code still takes the Paystack path.
  const isFreeCart = totalCents === 0;
  // Net-zero on the pure Vertu path (raw-free OR fully covered by codes/gift
  // card): confirms in-widget, no Paystack. A mixed cart still owes the ChiDesk
  // deposit, so it's never net-zero here.
  const isNetZeroVertu = !isChideskOnly && !isMixedCart && vertuNetCents === 0;
  // An earlier payment went through (the second-charge guard): Pay is off
  // and says so, on every surface.
  const submitLabel = guard?.code === "earlier_payment_refunded"
    ? // The payment that "went through" is being refunded (its booking could
      // not be kept): never "Payment received" beside the refund.
      "Payment being refunded"
    : payBlockedByGuard(guard)
    ? "Payment received"
    : pending
    ? isChideskOnly
      ? chideskHasDeposit
        ? "Redirecting to Paystack…"
        : "Booking…"
      : isNetZeroVertu
        ? "Confirming…"
        : "Redirecting to Paystack…"
    : isChideskOnly
      ? chideskHasDeposit
        ? `Pay ${currency} ${(chideskDepositCents! / 100).toLocaleString()} deposit`
        : `Book ${chideskItems.length} treatment${chideskItems.length === 1 ? "" : "s"}`
      : isMixedCart
        ? `Pay ${formatDue(currency, mixedDueNowCents)} with Paystack`
        : isNetZeroVertu
          ? "Confirm booking"
          : `Pay ${formatDue(currency, vertuNetCents)} with Paystack`;

  // Promo-code / gift-card Apply and Remove (PromoCodeSection's buttons).
  function applyCode(): Promise<void> {
    return applyCodeValue(codeInput);
  }
  async function applyCodeValue(code: string, fromLink = false): Promise<void> {
    if (!code.trim() || !firstBookingId) return;
    setCodeError(null);
    setCodePending(true);
    const res: CodePreview = await previewCode({
      tenantId: tenant.id,
      code,
      bookingId: firstBookingId,
      vouchersOnly: fromLink,
    });
    setCodePending(false);
    if (!res.ok) {
      // A link's code that cannot apply is named with the reason, so the guest
      // sees what the link offered and why the total is unchanged. It is never
      // put in the box: Apply there also tries gift cards, which a link's code
      // must never become.
      setCodeError(fromLink ? `${code}: ${describeCodeReason(res.reason)}` : describeCodeReason(res.reason));
      return;
    }
    if (appliedCodes.some((c) => c.code === res.code)) {
      setCodeError("Code already applied.");
      return;
    }
    setAppliedCodes((prev) => [
      ...prev,
      {
        code: res.code,
        kind: res.kind,
        discount_cents:
          res.kind === "gift_card"
            ? Math.min(res.discount_cents, Math.max(0, totalCents - discountCents))
            : res.discount_cents,
      },
    ]);
    setCodeInput("");
  }
  function removeCode(code: string) {
    setAppliedCodes((prev) => prev.filter((c) => c.code !== code));
  }

  // A booking link's promo code (?promo= / data-promo, link-promo.ts) is
  // applied for the guest once, as soon as this checkout takes codes: never
  // again after the guest removes it, and a code that failed is not retried.
  // The server prices the payment from the codes it is sent, as for a typed one.
  const linkCode = linkPromoFor(tenant.slug);
  const linkCodeTried = useRef(false);
  // The one code applied is the link's own: a reopened checkout may then say
  // it is back on (it re-applies itself), never for a code the guest typed.
  const onlyLinkCode =
    linkCode !== null && appliedCodes.length === 1 && appliedCodes[0]!.kind !== "gift_card" && appliedCodes[0]!.code === linkCode;
  useEffect(() => {
    if (
      !shouldApplyLinkPromo({
        code: linkCode,
        codesAccepted,
        chargedNowCents: totalCents,
        alreadyTried: linkCodeTried.current,
        appliedCount: appliedCodes.length,
      })
    )
      return;
    linkCodeTried.current = true;
    void applyCodeValue(linkCode!, true);
    // applyCodeValue is a fresh closure each render; this runs once per checkout.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [linkCode, codesAccepted, totalCents, appliedCodes.length]);

  return {
    tenant,
    firstName,
    setFirstName,
    lastName,
    setLastName,
    email,
    setEmail,
    phone,
    setPhone,
    phoneError,
    setPhoneError,
    notes,
    setNotes,
    country,
    setCountry,
    hasAccommodation,
    participantSignature,
    participantsValid,
    setParticipantsValid,
    participantHandle,
    error,
    setError,
    guard,
    bookAnother,
    pending,
    checkoutFormRef,
    holdEnded,
    setHoldEnded,
    paymentLapsed,
    // The full page's one-rider save found the hold ended (not_authorized /
    // hold_expired): Pay and the steps stop, as after a refused payment.
    markLapsed: () => setPaymentLapsed(true),
    codesAccepted,
    appliedCodes,
    linkCode,
    onlyLinkCode,
    codeInput,
    setCodeInput,
    codeError,
    codePending,
    applyCode,
    removeCode,
    loyaltySummary,
    setLoyaltySummary,
    appliedRedemption,
    setAppliedRedemption,
    contactId,
    firstBookingId,
    firstBookingReference,
    chideskItems,
    vertuItems,
    isChideskOnly,
    isMixedCart,
    cohostPairing,
    cohostUnmet,
    consentTerms,
    setConsentTerms,
    consentMarketing,
    setConsentMarketing,
    perItem,
    ensureContact,
    patchContact,
    ticketCents,
    requiredCents,
    depositCents,
    depositLines,
    totalCents,
    discountCents,
    chideskTotalCents,
    depositPreviewFailed,
    chideskDepositCents,
    submit,
    chideskHasDeposit,
    vertuNetCents,
    mixedDueNowCents,
    isFreeCart,
    isNetZeroVertu,
    submitLabel,
  };
}

/** The numbers a refusal can carry: event_minimum_not_met sends the event's
 *  `minimum` and the cart's `quantity` of its tickets (20260930100010). */
export type RefusalNumbers = { minimum?: number; quantity?: number };

export function refusalNumbers(result: object): RefusalNumbers {
  const r = result as Record<string, unknown>;
  return {
    minimum: typeof r.minimum === "number" ? r.minimum : undefined,
    quantity: typeof r.quantity === "number" ? r.quantity : undefined,
  };
}

export function describeConfirmReason(
  reason: string,
  serviceName?: string | null,
  noun: { one: string; other: string } = { one: "guest", other: "guests" },
  numbers?: RefusalNumbers,
): string {
  switch (reason) {
    case "participant_details_required":
      return `Please complete and save each ${noun.one}'s details above before continuing.`;
    // Booking questions (20260926090020): the statements are ticked, and
    // accepted, before any payment starts.
    case "checkout_confirmations_required":
      return "Please tick each confirmation before paying.";
    case "confirmations_changed":
      return "The booking terms were just updated. Please review and tick them again.";
    // The server refuses to start any payment once a hold has run out
    // (20260927156010: hold_expired, before the expiry sweep; the sweep turns
    // the same cart into no_pending_bookings). The hold timer's own words, so
    // the guest reads one sentence for one fact.
    case "no_pending_bookings":
    case "hold_expired":
      return LAPSE_MESSAGE;
    // Server-side cohost gate (tenant fallback='block'): a required-cohost
    // ticket had no pending sibling booking of the required service.
    case "cohost_required":
      return `Your ticket for this event requires a ${serviceName ?? "companion service"} booking — add it to your cart first.`;
    case "underpaid":
      return "The required amount for this booking wasn't covered.";
    // Event menus (20260930100010): the cart holds fewer tickets for an event
    // than its minimum per booking; or another group took a private sitting.
    case "event_minimum_not_met":
      return minimumNotMetMessage(numbers?.minimum, numbers?.quantity);
    case "event_already_booked":
      return ALREADY_BOOKED_MESSAGE;
    // One menu per booking (20260930170010): the cart holds two different
    // normal menus for a one-menu-per-booking sitting.
    case "event_one_menu_per_booking":
      return ONE_MENU_PER_BOOKING_MESSAGE;
    case "invalid_cart_token":
      return "Your cart session is invalid. Start again.";
    default:
      return `Couldn't confirm (${reason}).`;
  }
}

// The second-charge guard's refusals (20260928090040) are notices, not
// errors: see state/charge-guard.ts, refuseInit above and GuardNotice.
export { describeInitError } from "../../state/paystack-init-error";

export function describeCodeReason(reason: string): string {
  switch (reason) {
    case "not_found":
      return "That code isn't recognised.";
    case "disabled":
      return "That code is paused.";
    case "expired":
      return "That code has expired.";
    case "not_yet_valid":
      return "That code isn't active yet.";
    case "max_uses_reached":
      return "That code has been fully claimed.";
    case "service_not_eligible":
    case "service_type_not_eligible":
      return "That code doesn't apply to this service.";
    case "below_min_spend":
      return "Your booking is below this code's minimum spend.";
    case "guest_cap_reached":
      return "You've already used this code the maximum number of times.";
    case "depleted":
      return "That gift card has no balance left.";
    case "cancelled":
      return "That gift card has been cancelled.";
    case "code_required":
      return "Enter a code first.";
    default:
      return `Couldn't apply the code (${reason}).`;
  }
}
